// Header Collection Count Animation
// Animates product count fade-in and count-up on hover

class HeaderCollectionCount {
  constructor() {
    this.init();
  }

  init() {
    // Get all collection count elements
    const countElements = document.querySelectorAll('.header__nav-link-count');

    if (countElements.length === 0) return;

    // Set up hover listeners for parent links
    const navLinks = document.querySelectorAll(
      '.header__nav-link, .header__nav-dropdown-link'
    );

    navLinks.forEach((link) => {
      const countElement = link.querySelector('.header__nav-link-count');
      if (!countElement) return;

      const targetCount = parseInt(countElement.textContent.trim());
      if (isNaN(targetCount)) return;

      // Store target count on element for fade-out
      countElement.dataset.targetCount = targetCount;

      // Reset count for animation
      countElement.textContent = '0';
      countElement.style.opacity = '0';
      countElement.style.transform = 'translateY(-4px)';

      link.addEventListener('mouseenter', () => {
        this.animateCount(countElement, targetCount);
      });

      link.addEventListener('mouseleave', () => {
        this.fadeOutCount(countElement);
      });
    });

    // Also handle dropdown items
    const dropdownItems = document.querySelectorAll(
      '.header__nav-links-item, .header__nav-sublinks-item'
    );

    dropdownItems.forEach((item) => {
      const link = item.querySelector('a');
      const countElement = link?.querySelector('.header__nav-link-count');
      if (!countElement) return;

      const targetCount = parseInt(countElement.textContent.trim());
      if (isNaN(targetCount)) return;

      // Store target count on element for fade-out
      countElement.dataset.targetCount = targetCount;

      // Reset count for animation
      countElement.textContent = '0';
      countElement.style.opacity = '0';
      countElement.style.transform = 'translateY(-4px)';

      item.addEventListener('mouseenter', () => {
        this.animateCount(countElement, targetCount);
      });

      item.addEventListener('mouseleave', () => {
        this.fadeOutCount(countElement);
      });
    });
  }

  animateCount(element, targetCount) {
    // Cancel any existing animation (fade-in or fade-out)
    if (element.animationId) {
      const idToCancel = element.animationId;
      if (typeof idToCancel === 'number') {
        cancelAnimationFrame(idToCancel);
      }
      delete element.animationId;
    }

    // Always start from a clean state for reliable animation
    element.textContent = '0';
    element.style.opacity = '0';
    element.style.transform = 'translateY(-4px)';
    element.classList.remove('is-visible');

    const duration = 600; // Animation duration in ms
    const startTime = performance.now();
    const startCount = 0;

    // Set animation ID before starting
    element.animationId = 'animating';

    const animate = (currentTime) => {
      // Check if animation was cancelled
      if (!element.animationId || element.animationId === 'cancelled') {
        delete element.animationId;
        return;
      }

      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Easing function (ease-out)
      const easeOut = 1 - Math.pow(1 - progress, 3);

      // Calculate current count
      const currentCount = Math.floor(
        startCount + (targetCount - startCount) * easeOut
      );
      element.textContent = currentCount;

      // Fade in and slide up
      if (progress < 0.5) {
        // Faster fade-in during first half
        const fadeProgress = Math.min(progress * 2, 1);
        element.style.opacity = fadeProgress * 0.7;
        element.style.transform = `translateY(${-4 * (1 - fadeProgress)}px)`;
      } else {
        element.style.opacity = '0.7';
        element.style.transform = 'translateY(0)';
      }

      if (progress < 1) {
        element.animationId = requestAnimationFrame(animate);
      } else {
        // Ensure final values
        element.textContent = targetCount;
        element.style.opacity = '0.7';
        element.style.transform = 'translateY(0)';
        element.classList.add('is-visible');
        delete element.animationId;
      }
    };

    element.animationId = requestAnimationFrame(animate);
  }

  fadeOutCount(element) {
    // Cancel any existing animation
    if (element.animationId) {
      const idToCancel = element.animationId;
      if (typeof idToCancel === 'number') {
        cancelAnimationFrame(idToCancel);
      }
      delete element.animationId;
    }

    // Get current state for smooth fade-out
    const startOpacity = parseFloat(element.style.opacity) || 0;
    const currentCount = parseInt(element.textContent) || 0;

    // If already hidden or count is 0, reset immediately
    if (startOpacity === 0 || currentCount === 0) {
      element.textContent = '0';
      element.style.opacity = '0';
      element.style.transform = 'translateY(-4px)';
      element.classList.remove('is-visible');
      return;
    }

    const duration = 250; // Fade-out duration in ms (faster than fade-in)
    const startTime = performance.now();

    const animate = (currentTime) => {
      // Check if animation was cancelled
      if (!element.animationId) {
        return;
      }

      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Easing function (ease-in for fade-out)
      const easeIn = progress * progress;

      // Fade out and slide down
      const opacity = startOpacity * (1 - easeIn);
      const translateY = -4 * easeIn;

      element.style.opacity = opacity;
      element.style.transform = `translateY(${translateY}px)`;

      // Count down (optional, faster count-down)
      if (progress < 0.6 && currentCount > 0) {
        const countProgress = progress / 0.6;
        const newCount = Math.max(
          0,
          Math.floor(currentCount * (1 - countProgress))
        );
        element.textContent = newCount;
      } else {
        element.textContent = '0';
      }

      if (progress < 1) {
        element.animationId = requestAnimationFrame(animate);
      } else {
        // Ensure final values
        element.textContent = '0';
        element.style.opacity = '0';
        element.style.transform = 'translateY(-4px)';
        element.classList.remove('is-visible');
        delete element.animationId;
      }
    };

    element.animationId = requestAnimationFrame(animate);
  }
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new HeaderCollectionCount();
  });
} else {
  new HeaderCollectionCount();
}

// Re-initialize when sections load (Shopify theme editor)
document.addEventListener('shopify:section:load', () => {
  new HeaderCollectionCount();
});
